import React, {PureComponent} from 'react';

import classNames from 'classnames';

import s from './timer.scss';

type PropsType = {
    /** callback on start timer */
    onStart: () => void;
    /** callback on finish timer */
    onFinish: () => void;
    /** unix timestamp seconds */
    endTime: number;
    /** can be small, big by default */
    size: string;
    /** children can be provided for timer view customization */
    children?(arg: {seconds: string; minutes: string; hours: string}): JSX.Element;
};

type StateType = {
    seconds: string;
    minutes: string;
    hours: string;
};

/**
 * Display timer
 */
export class Timer extends PureComponent<PropsType, StateType> {
    static defaultProps = {
        endTime: 0,
        onFinish: () => {},
        onStart: () => {},
        size: 'medium',
    };

    state = {
        hours: '00',
        minutes: '00',
        seconds: '00',
    };

    /**
     * Sets end time and remember interval id
     */
    componentDidMount() {
        const {onStart} = this.props;
        this.tick();
        this.intervalId = setInterval(this.tick, 1000);
        onStart();
    }

    /**
     * Updates timer when new "endTime" is arrived
     * @param {PropsType} prevProps - previous properties
     */
    componentDidUpdate(prevProps: PropsType) {
        if (prevProps.endTime !== this.props.endTime) {
            clearInterval(this.intervalId);
            this.tick();
            this.intervalId = setInterval(this.tick, 1000);
        }
    }

    /**
     * Method is invoked immediately before a component is unmounted and destroyed.
     */
    componentWillUnmount() {
        clearInterval(this.intervalId);
    }

    intervalId: ReturnType<typeof setInterval> | undefined;

    /**
     * Calculate timer
     */
    tick = () => {
        const endSeconds = this.props.endTime - Date.now() / 1000;

        if (endSeconds <= 0) {
            clearInterval(this.intervalId);
            this.props.onFinish();
        } else {
            const seconds = Math.floor(endSeconds % 60);
            const minutes = Math.floor((endSeconds / 60) % 60);
            const hours = Math.floor((endSeconds / 3600) % 60);
            this.setState({
                hours: `0${hours}`.slice(-2),
                minutes: `0${minutes}`.slice(-2),
                seconds: `0${seconds}`.slice(-2),
            });
        }
    };

    /**
     * Render jsx to html
     * @returns {JSX.Element} Rendered react component
     */
    render() {
        const {hours, minutes, seconds} = this.state;
        const {children, size} = this.props;

        return children ? (
            children({hours, minutes, seconds})
        ) : (
            <div className={classNames(s.timer, {[s.smallTimer]: size === 'small'})}>
                <span className={s.number}>{minutes.charAt(0)}</span>
                <span className={s.number}>{minutes.charAt(1)}</span>
                <span className={s.delimeter}>:</span>
                <span className={s.number}>{seconds.charAt(0)}</span>
                <span className={s.number}>{seconds.charAt(1)}</span>
            </div>
        );
    }
}
